import React from 'react';

import { margin, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { EditLinkPreviewDialog } from '@lumapps/lumx-preview/components/LinkPreview/EditLinkPreviewDialog';
import type { LinkPreviewMetadata } from '@lumapps/lumx-preview/types';
import { mdiAutorenew, mdiClose, mdiLink, mdiPencil } from '@lumapps/lumx/icons';
import { Alignment, FlexBox, Icon, Kind, Message, Size, Theme } from '@lumapps/lumx/react';
import { useDimensions } from '@lumapps/responsive';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';
import { ElementToolbar } from '@lumapps/wrex/components/ElementToolbar';
import { WREX_CORE } from '@lumapps/wrex/keys';
import { ReactEditor, Transforms, useSelected, useSlateStatic } from '@lumapps/wrex/slate';
import { useInlineVoid } from '@lumapps/wrex/slate/utils/useInlineVoid';
import type { ElementRender, ToolbarItem, WrexEditor } from '@lumapps/wrex/types';

import { EXTERNAL_RESOURCE_IMG_CONFIG, LINK_PREVIEW, PREVIEW_MAX_LENGTH } from '../../../constants';
import { WREX_LINK_PREVIEW } from '../../../keys';
import type { LinkPreviewEditor, LinkPreviewEditorOptions, LinkPreviewElement, Resource } from '../../../types';
import { handlePreviewChange } from '../../../utils/handlePreviewChange';
import { CLASSNAME, LinkPreview, RESOURCES_CONFIG } from '../../blocks/LinkPreview';
import { useResourceLinkProps } from '../../blocks/LinkPreview/useResourceLinkProps';
import { ReplaceResourcePreviewDialog } from '../../dialog/ReplaceResourcePreviewDialog';

import './index.scss';

type EditableLinkPreviewType = ElementRender<LinkPreviewElement & LinkPreviewEditorOptions, HTMLDivElement>;

const SCOPE = LINK_PREVIEW;
// a column max width when only 2 columns are added in rich text
const TWO_COLUMNS_MAX_WIDTH = 290;
/**
 * Display a link preview with actions buttons to either edit, replace or delete the preview item
 */
export const EditableLinkPreview: EditableLinkPreviewType = ({
    element,
    elementRef,
    children,
    className,
    // we don't use uploadMediaOptions but we don't want to forward it
    // eslint-disable-next-line unused-imports/no-unused-vars
    uploadMediaOptions,
    ...forwardedProps
}) => {
    const currentElement = element as LinkPreviewElement;
    const editorStatic = useSlateStatic() as WrexEditor<LinkPreviewEditor>;
    const selected = useSelected();
    const { onDelete } = useInlineVoid(editorStatic, currentElement);
    const { translateKey } = useTranslate();
    const { block, element: elementClass } = useClassnames(CLASSNAME);
    const [isEditDialogOpen, toggleEditDialog, closeEditDialog] = useBooleanState(false);
    const [isReplaceDialogOpen, toggleReplaceDialog, closeReplaceDialog] = useBooleanState(false);
    const linkPreviewOptions = editorStatic.getLinkPreviewOptions();
    const { get } = useDataAttributes(SCOPE);
    const { currentBreakpoint, ref: dimRef } = useDimensions({
        breakpoints: { reduced: 0, full: TWO_COLUMNS_MAX_WIDTH },
    });

    const { resource, url } = currentElement;
    const parentRef = React.useRef(null);

    const path = ReactEditor.findPath(editorStatic, currentElement);
    const pathRef = editorStatic.pathRef(path);

    const customLink = useResourceLinkProps(resource, url);

    const visibilityMessage = (
        <Message className={margin('top')} kind={Kind.info} hasBackground>
            {translateKey(WREX_CORE.THUMBNAIL_VISIBILITY)}
        </Message>
    );

    const thumbnailFallback = (
        <FlexBox as="span" hAlign={Alignment.center} vAlign={Alignment.center}>
            <Icon icon={resource ? RESOURCES_CONFIG.icons[resource.type] : mdiLink} size={Size.m} theme={Theme.light} />
        </FlexBox>
    );

    /**
     * Computes preview object to forward to EditLinkPreviewDialog
     */
    const computePreviewElement = (
        currentEl: LinkPreviewElement,
    ): Partial<LinkPreviewMetadata> & { resource?: Resource } => {
        const { description, title, url, thumbnailUrl, resource } = currentEl;
        const image = thumbnailUrl || resource?.thumbnailUrl;

        return {
            title: title || resource?.title,
            description: description || resource?.description,
            images: image ? [image] : [],
            url,
            thumbnailIndex: 0,
            resource,
        };
    };

    const staticOptions: ToolbarItem[] = [
        {
            type: 'option',
            icon: mdiClose,
            itemKey: 'delete-link-preview',
            label: translateKey(GLOBAL.DELETE),
            variant: 'icon',
            onClick: onDelete,
            otherProps: get({ element: 'button', action: 'delete' }),
        },
    ];

    const section: ToolbarItem = {
        type: 'section',
        label: '',
        itemKey: 'link-preview-options-section',
        childrenOptions: [
            {
                type: 'option',
                icon: mdiPencil,
                itemKey: 'edit-link-preview',
                variant: 'icon',
                label: translateKey(GLOBAL.EDIT),
                onClick: toggleEditDialog,
                otherProps: get({ element: 'button', action: 'edit' }),
            },
            ...(resource
                ? [
                      {
                          type: 'option' as const,
                          icon: mdiAutorenew,
                          itemKey: 'replace-link-preview',
                          variant: 'icon' as const,
                          label: translateKey(GLOBAL.REPLACE),
                          onClick: toggleReplaceDialog,
                          otherProps: get({ element: 'button', action: 'replace' }),
                      },
                  ]
                : []),
        ],
    };

    /**
     * We don't use onFocus from useInlineVoid in here, because for some reason, focusAt used in
     * focus function closes the dropdown when clicking on "more options" button in link-preview toolbar
     * (when link-preview is inside 4 columns we display the more options button)
     */
    const onFocus = React.useCallback(() => {
        const path = ReactEditor.findPath(editorStatic, currentElement);
        const point = { path: [...path, 0], offset: 0 };
        // update Slate selection
        Transforms.select(editorStatic, point);
    }, [currentElement, editorStatic]);

    return (
        <>
            <LinkPreview
                {...forwardedProps}
                className={block({ editable: true, selected })}
                onFocus={onFocus}
                tabIndex={0}
                element={element}
                elementRef={mergeRefs([elementRef, dimRef])}
            >
                {children}
                <ElementToolbar
                    className={elementClass('actions')}
                    toolbarOptions={[section]}
                    staticOptions={staticOptions}
                    dataScope={SCOPE}
                    toolbarAriaLabel={translateKey(WREX_LINK_PREVIEW.TOOLBAR)}
                    currentBreakpoint={currentBreakpoint}
                    menuBarRef={parentRef}
                    size={currentBreakpoint === 'reduced' ? Size.s : Size.m}
                />
            </LinkPreview>

            <ReplaceResourcePreviewDialog
                isOpen={isReplaceDialogOpen}
                onClose={closeReplaceDialog}
                onChange={(link) => handlePreviewChange(editorStatic, pathRef, link)}
                parentElement={parentRef}
                resourceType={resource?.type}
            />

            <EditLinkPreviewDialog
                className={className}
                editImageOptions={{
                    className: margin('top', Size.huge),
                    thumbnailProps: {
                        ...EXTERNAL_RESOURCE_IMG_CONFIG,
                    },
                    after: linkPreviewOptions.uploadMediaOptions.arePublic === true ? visibilityMessage : undefined,
                    uploadMediaOptions: linkPreviewOptions.uploadMediaOptions,
                }}
                isOpen={isEditDialogOpen}
                onChange={(link) => handlePreviewChange(editorStatic, pathRef, link)}
                onClose={closeEditDialog}
                parentElement={parentRef}
                preview={computePreviewElement(currentElement)}
                descriptionFieldProps={{
                    maxLength: PREVIEW_MAX_LENGTH,
                }}
                previewImgProps={{
                    fallback: thumbnailFallback,
                    ...EXTERNAL_RESOURCE_IMG_CONFIG,
                }}
                title={resource ? translateKey(WREX_LINK_PREVIEW.EDIT_RESOURCE_PREVIEW) : undefined}
                titleFieldProps={{
                    maxLength: PREVIEW_MAX_LENGTH,
                }}
                customLink={customLink}
                // we display an image in the uploader only when the resource/link thumbnail has been overridden. Otherwise the uploader should display without any image
                selectedMedia={
                    element?.thumbnailUrl && element?.thumbnailUrl !== resource?.thumbnailUrl
                        ? {
                              url: element?.thumbnailUrl,
                          }
                        : undefined
                }
            />
        </>
    );
};
EditableLinkPreview.displayName = LINK_PREVIEW;

export const editableLinkPreviewWithOptions = (options: LinkPreviewEditorOptions) => {
    const Enhanced: EditableLinkPreviewType = (props) => <EditableLinkPreview {...props} {...options} />;
    Enhanced.displayName = EditableLinkPreview.displayName;

    return Enhanced;
};
